import CryptoJS from "crypto-js";

/**
 * 签名工具类
 * 提供多种签名算法和加密功能
 */
export class SignUtils {
  /**
   * 生成MD5哈希
   * @param data 要哈希的数据
   * @returns MD5哈希字符串
   */
  static md5(data: string): string {
    return CryptoJS.MD5(data).toString();
  }

  /**
   * 生成SHA1哈希
   * @param data 要哈希的数据
   * @returns SHA1哈希字符串
   */
  static sha1(data: string): string {
    return CryptoJS.SHA1(data).toString();
  }

  /**
   * 生成SHA256哈希
   * @param data 要哈希的数据
   * @returns SHA256哈希字符串
   */
  static sha256(data: string): string {
    return CryptoJS.SHA256(data).toString();
  }

  /**
   * 生成SHA512哈希
   * @param data 要哈希的数据
   * @returns SHA512哈希字符串
   */
  static sha512(data: string): string {
    return CryptoJS.SHA512(data).toString();
  }

  /**
   * 生成HMAC-SHA256签名
   * @param data 要签名的数据
   * @param secret 密钥
   * @returns HMAC签名字符串
   */
  static hmacSha256(data: string, secret: string): string {
    return CryptoJS.hmac.SHA256(data, secret).toString();
  }

  /**
   * 生成HMAC-SHA512签名
   * @param data 要签名的数据
   * @param secret 密钥
   * @returns HMAC签名字符串
   */
  static hmacSha512(data: string, secret: string): string {
    return CryptoJS.hmac.SHA512(data, secret).toString();
  }

  /**
   * AES加密
   * @param data 要加密的数据
   * @param key 加密密钥
   * @param iv 初始化向量（可选）
   * @returns 加密后的字符串
   */
  static aesEncrypt(data: string, key: string, iv?: string): string {
    const keyBytes = CryptoJS.enc.Utf8.parse(key);
    const ivBytes = iv
      ? CryptoJS.enc.Utf8.parse(iv)
      : CryptoJS.enc.Utf8.parse(key.substring(0, 16));

    const encrypted = CryptoJS.AES.encrypt(data, keyBytes);

    return encrypted.toString();
  }

  /**
   * AES解密
   * @param encryptedData 加密的数据
   * @param key 解密密钥
   * @param iv 初始化向量（可选）
   * @returns 解密后的字符串
   */
  static aesDecrypt(encryptedData: string, key: string, iv?: string): string {
    const keyBytes = CryptoJS.enc.Utf8.parse(key);

    const decrypted = CryptoJS.AES.decrypt(encryptedData, keyBytes);

    return decrypted.toString(CryptoJS.enc.Utf8);
  }

  /**
   * 生成API请求签名
   * @param params 请求参数
   * @param secret 签名密钥
   * @param timestamp 时间戳
   * @returns 签名字符串
   */
  static generateApiSignature(
    token: string,
    userId: string,
    secret: string
  ): string {
    const time = Math.floor(Date.now() / 1000);
    const signString = `${token}${secret}${userId}${time}`;
    return this.md5(signString);
  }

  /**
   * 生成设备指纹签名
   * @param deviceInfo 设备信息
   * @param secret 签名密钥
   * @returns 设备指纹签名
   */
  static generateDeviceSignature(
    deviceInfo: Record<string, any>,
    secret: string
  ): string {
    const deviceString = JSON.stringify(deviceInfo);
    return this.hmacSha256(deviceString, secret);
  }

  /**
   * 生成用户会话签名
   * @param userId 用户ID
   * @param sessionId 会话ID
   * @param timestamp 时间戳
   * @param secret 签名密钥
   * @returns 会话签名
   */
  static generateSessionSignature(
    userId: string,
    sessionId: string,
    timestamp: number,
    secret: string
  ): string {
    const sessionString = `${userId}:${sessionId}:${timestamp}`;
    return this.hmacSha256(sessionString, secret);
  }

  /**
   * 验证签名
   * @param data 原始数据
   * @param signature 签名
   * @param secret 密钥
   * @param algorithm 算法类型
   * @returns 是否验证通过
   */
  static verifySignature(
    data: string,
    signature: string,
    secret: string,
    algorithm: "sha256" | "sha512" = "sha256"
  ): boolean {
    let expectedSignature: string;

    if (algorithm === "sha256") {
      expectedSignature = this.hmacSha256(data, secret);
    } else {
      expectedSignature = this.hmacSha512(data, secret);
    }

    return expectedSignature === signature;
  }

  /**
   * 生成随机字符串
   * @param length 字符串长度
   * @returns 随机字符串
   */
  static generateRandomString(length: number = 32): string {
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";

    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }

    return result;
  }

  /**
   * 生成安全的随机密钥
   * @param length 密钥长度
   * @returns 随机密钥
   */
  static generateSecureKey(length: number = 64): string {
    return this.generateRandomString(length);
  }

  /**
   * 生成时间戳
   * @returns 当前时间戳（毫秒）
   */
  static generateTimestamp(): number {
    return Date.now();
  }

  /**
   * 生成ISO格式时间字符串
   * @returns ISO格式时间字符串
   */
  static generateISOTimestamp(): string {
    return new Date().toISOString();
  }
}

/**
 * 密码加密工具
 */
export class PasswordUtils {
  /**
   * 加密密码（使用MD5）
   * @param password 原始密码
   * @returns 加密后的密码
   */
  static encryptPassword(password: string): string {
    return SignUtils.md5(password);
  }

  /**
   * 加密密码（使用SHA256 + 盐值）
   * @param password 原始密码
   * @param salt 盐值
   * @returns 加密后的密码
   */
  static encryptPasswordWithSalt(password: string, salt: string): string {
    return SignUtils.sha256(password + salt);
  }

  /**
   * 生成密码盐值
   * @param length 盐值长度
   * @returns 随机盐值
   */
  static generateSalt(length: number = 16): string {
    return SignUtils.generateRandomString(length);
  }

  /**
   * 验证密码
   * @param password 原始密码
   * @param encryptedPassword 加密后的密码
   * @param salt 盐值（如果使用）
   * @returns 是否匹配
   */
  static verifyPassword(
    password: string,
    encryptedPassword: string,
    salt?: string
  ): boolean {
    if (salt) {
      return this.encryptPasswordWithSalt(password, salt) === encryptedPassword;
    }
    return this.encryptPassword(password) === encryptedPassword;
  }
}

/**
 * 应用特定的签名工具
 * 使用项目密钥进行签名和加密
 */
export class AppSignUtils {
  // 应用密钥 - 从环境变量读取
  public static get APP_SECRET(): string {
    // 在客户端和服务端都能访问的环境变量
    const secret =
      "#MyH8086.g9527~"; // 默认值作为后备


    return secret;
  }

  /**
   * 生成应用API签名
   * @param params 请求参数
   * @param timestamp 时间戳
   * @returns 签名字符串
   */
  static generateAppSignature(
    params: Record<string, any>,
    timestamp?: number
  ): string {
    const ts = timestamp || SignUtils.generateTimestamp();
    return SignUtils.generateApiSignature(params, this.APP_SECRET, ts);
  }

  /**
   * 生成设备认证签名
   * @param deviceInfo 设备信息
   * @returns 设备认证签名
   */
  static generateDeviceAuthSignature(deviceInfo: Record<string, any>): string {
    return SignUtils.generateDeviceSignature(deviceInfo, this.APP_SECRET);
  }

  /**
   * 生成用户会话签名
   * @param userId 用户ID
   * @param sessionId 会话ID
   * @param timestamp 时间戳
   * @returns 会话签名
   */
  static generateUserSessionSignature(
    userId: string,
    sessionId: string,
    timestamp?: number
  ): string {
    const ts = timestamp || SignUtils.generateTimestamp();
    return SignUtils.generateSessionSignature(
      userId,
      sessionId,
      ts,
      this.APP_SECRET
    );
  }

  /**
   * 加密敏感数据
   * @param data 要加密的数据
   * @returns 加密后的数据
   */
  static encryptSensitiveData(data: string): string {
    return SignUtils.aesEncrypt(data, this.APP_SECRET);
  }

  /**
   * 解密敏感数据
   * @param encryptedData 加密的数据
   * @returns 解密后的数据
   */
  static decryptSensitiveData(encryptedData: string): string {
    return SignUtils.aesDecrypt(encryptedData, this.APP_SECRET);
  }

  /**
   * 验证应用签名
   * @param data 原始数据
   * @param signature 签名
   * @returns 是否验证通过
   */
  static verifyAppSignature(data: string, signature: string): boolean {
    return SignUtils.verifySignature(data, signature, this.APP_SECRET);
  }
}

/**
 * 导出默认实例
 */
export default SignUtils;
